/**
 * Portal utility for Svelte components
 *
 * Moves a DOM element to document.body to escape CSS stacking contexts
 * created by parent elements (overflow, transform, filter, etc.)
 *
 * This is useful for modals, dialogs, and tooltips that need to appear
 * above all other content regardless of their position in the DOM tree.
 *
 * @example
 * ```svelte
 * <dialog use:portal>
 *   Modal content
 * </dialog>
 * ```
 */
export function portal(node: HTMLElement, target?: string) {
  const container = target
    ? (document.querySelector(target) ?? document.body)
    : document.body;
  container.appendChild(node);

  return {
    update(newTarget?: string) {
      const newContainer = newTarget
        ? (document.querySelector(newTarget) ?? document.body)
        : document.body;
      if (node.parentNode !== newContainer) {
        newContainer.appendChild(node);
      }
    },
    destroy() {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    },
  };
}
